import { MedicalNecessityDsm5Criteria } from "@utils/constants";
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  differenceInMonths,
  differenceInYears,
  format,
  isValid,
  parse,
} from "date-fns";
import { GraphQLError } from "graphql";
import { useNavigate } from "react-router";

import { ApiLearnerAvailabilityTimeOfDayChoices, ReportTypeEnums } from "@api/graphql/types-and-hooks";
import { useAdminData } from "@providers/AdminDataProvider";

export const capitalizeString = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.toLowerCase().slice(1);
};

export const toTitleCase = (str?: string | null): string =>
  str?.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) ?? "";

export const getTimeByShift = (shiftChoice: ApiLearnerAvailabilityTimeOfDayChoices): string => {
  switch (shiftChoice) {
    case "MORNING":
      return "8:00am - 12:00pm";
    case "AFTERNOON":
      return "12:00pm - 3:00pm";
    case "MIDDAY":
      return "3:00pm - 5:30pm";
    case "EVENING":
      return "5:30pm - 7:30pm";
    default:
      return "";
  }
};

export function nonNullable<T>(value: T): value is NonNullable<T> {
  return value !== null && value !== undefined;
}

export const convertHeadersToObject = (headers: HeadersInit): Record<string, string> => {
  const result: Record<string, string> = {};

  if (Array.isArray(headers)) {
    headers.forEach(([key, value]) => {
      result[key] = value;
    });
  } else if (headers instanceof Headers) {
    headers.forEach((value, key) => {
      result[key] = value;
    });
  } else {
    Object.assign(result, headers);
  }

  return result;
};

export const getDifferenceInYearsAndMonths = (startDate: Date, endDate: Date): string => {
  const years = differenceInYears(endDate, startDate);
  const adjustedStartDate = addYears(startDate, years);
  const months = differenceInMonths(endDate, adjustedStartDate);

  return `${years} years ${months ? `${months} months` : ""}`;
};

export const getProjectedDate = (duration: number, unit: "days" | "weeks" | "months") => {
  if (!duration || !unit) return null;

  const now = new Date();
  let projected: Date;

  switch (unit.toLowerCase()) {
    case "days":
      projected = addDays(now, duration);
      break;
    case "weeks":
      projected = addWeeks(now, duration);
      break;
    case "months":
      projected = addMonths(now, duration);
      break;
    default:
      return null;
  }

  return format(projected, "MMMM d, yyyy");
};

export const formatDateTime = (datetime: Date): string => {
  try {
    let formattedDate = format(datetime, "MM/dd/yyyy h:mm a");
    formattedDate = formattedDate.replace("AM", "A.M.").replace("PM", "P.M.");
    return formattedDate;
  } catch (_error) {
    return "-";
  }
};

export const formatDate = (datetime: Date | string): string => {
  try {
    let dateObj: Date | null = null;

    if (typeof datetime === "string") {
      // Try different common date formats
      const formats = [
        "yyyy-MM-dd", // 2023-01-31
        "yyyy-MM-dd'T'HH:mm:ss.SSSxxx", // ISO format
        "yyyy-MM-dd'T'HH:mm:ss", // ISO without timezone
        "MM/dd/yyyy", // 01/31/2023
        "dd/MM/yyyy", // 31/01/2023
        "MM-dd-yyyy", // 01-31-2023
      ];

      // Try each format until one works
      let parsed: Date | null = null;
      for (const fmt of formats) {
        parsed = parse(datetime, fmt, new Date());
        if (isValid(parsed)) {
          dateObj = parsed;
          break;
        }
      }

      // If none of the formats worked, try the built-in Date constructor
      if (!parsed || !isValid(parsed)) {
        const builtInParsed = new Date(datetime);
        if (isValid(builtInParsed)) {
          dateObj = builtInParsed;
        } else {
          throw new Error("Invalid date format");
        }
      }

      if (!dateObj) {
        throw new Error("Failed to parse date");
      }
    } else if (datetime instanceof Date) {
      dateObj = datetime;
    } else {
      throw new Error("Input must be a Date object or date string");
    }

    return format(dateObj, "MM/dd/yyyy");
  } catch (_error) {
    return "-";
  }
};

export const formatDateYYYY_MM_DD = (datetime: Date): string => {
  try {
    const formattedDate = format(datetime, "yyyy-MM-dd");

    return formattedDate;
  } catch (_error) {
    return "-";
  }
};

export const formatDurationToHrMin = (seconds: number): string => {
  const absSeconds = Math.abs(seconds);
  const hours = Math.floor(absSeconds / 3600);
  const minutes = Math.floor((absSeconds % 3600) / 60);

  return `${hours}hr ${minutes}min`;
};

export const formatDurationToMinSec = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return `${minutes}m ${remainingSeconds}s`;
};

const padToTwoDigits = (num: number): string => num.toString().padStart(2, "0");

export const formatSecondsToHHMMSS = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.round(seconds % 60);

  return `${padToTwoDigits(hours)}:${padToTwoDigits(minutes)}:${padToTwoDigits(remainingSeconds)}`;
};

export function formatDurationToDayHHMM(seconds: number): string {
  const days = Math.floor(seconds / (3600 * 24));
  const hours = Math.floor((seconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const parts = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes || parts.length === 0) parts.push(`${minutes}m`);

  return parts.join(" ");
}
export const formatVideoSecondsToHHMMSS = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.round(seconds % 60);

  if (hours === 0) return `${minutes}:${padToTwoDigits(remainingSeconds)}`;
  return `${hours}:${minutes}:${padToTwoDigits(remainingSeconds)}`;
};

export const isAuthorized = (list1: string[]): boolean | null => {
  const { roles } = useAdminData();
  if (roles.length) {
    const set1 = new Set(roles);
    for (const item of list1) {
      if (set1.has(item)) {
        return true;
      }
    }
    return false;
  }
  return false;
};

export type HeroIconType = React.ForwardRefExoticComponent<
  Omit<React.SVGProps<SVGSVGElement>, "ref"> & {
    title?: string | undefined;
    titleId?: string | undefined;
  } & React.RefAttributes<SVGSVGElement>
>;

// Helper type
export type RequiredFields<T, K extends keyof T> = T & Required<Pick<T, K>>;

export const graphqlErrorHandler = (error: GraphQLError) => {
  const errorMessage = error.message;
  const jsonStartIndex = errorMessage.indexOf("{");
  const jsonString = errorMessage.substring(jsonStartIndex);
  const errorData = JSON.parse(jsonString);

  interface GraphQLError {
    message: string;
  }

  const errorMessages = errorData.response.errors.map((err: GraphQLError) => err.message);

  return errorMessages;
};

export const useNavigateById = () => {
  const go = useNavigate();

  const scrollToDivById = (divId: string) => {
    if (divId && document.getElementById(divId)) {
      go(`${location.pathname}#${divId}`);
    }
  };

  return scrollToDivById;
};

/**
 * Scroll the element with the given id into view using the nearest scrollable ancestor
 * (window or a scroll container). Use this when the target may not be inside a
 * scrollable left panel (e.g. Review and Sign when the window scrolls).
 */
export const scrollToElementById = (elementId: string, block: ScrollLogicalPosition = "start"): void => {
  const el = document.getElementById(elementId);
  if (el) el.scrollIntoView({ behavior: "smooth", block });
};

const restartAnimation = (el: HTMLElement): void => {
  void el.offsetWidth;
};

const playAnimationOnce = (el: HTMLElement, className: string, keyframeName: string): void => {
  el.classList.remove(className);
  restartAnimation(el);
  el.classList.add(className);
  const onEnd = (event: AnimationEvent) => {
    if (event.animationName !== keyframeName) return;
    el.classList.remove(className);
    el.removeEventListener("animationend", onEnd);
  };
  el.addEventListener("animationend", onEnd);
};

/** Briefly wobble the element with the given id to draw the eye to it. */
export const shakeElementById = (elementId: string): void => {
  const el = document.getElementById(elementId);
  if (el) playAnimationOnce(el, "animate-shake", "shake");
};

export const indexToAlphabet = (index: number): string => {
  if (index < 0) throw new Error("Index must be a non-negative number.");

  let result = "";
  while (index >= 0) {
    const charCode = (index % 26) + 97; // 97 is the ASCII code for 'a'
    result = String.fromCharCode(charCode) + result;
    index = Math.floor(index / 26) - 1;
  }
  return result;
};

export const getHierarchicalIdentifier = (level1Index: number, level2Index?: number, level3Index?: number): string => {
  let identifier = `${level1Index + 1}`;
  if (level2Index !== undefined) {
    // Uppercase target letters (1A, 1B, …) per the unified numbering scheme.
    identifier += indexToAlphabet(level2Index).toUpperCase();
  }
  if (level3Index !== undefined) {
    identifier += `${level3Index + 1}`;
  }
  return `${identifier}. `;
};

/**
 * 0-based starting goal number for each group given each group's goal count
 * (prefix sums). Drives continuous goal numbering across domains (1..N): group i
 * starts at the running total of goals in the groups before it.
 */
export const getGoalStartIndices = (groupGoalCounts: number[]): number[] => {
  const starts: number[] = [];
  let running = 0;
  for (const count of groupGoalCounts) {
    starts.push(running);
    running += count;
  }
  return starts;
};

export const parseHyphenatedText = (input: string): string => {
  return input
    .replace(/-/g, " ") // Replace hyphens with spaces
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
};

export const convertChecklistToString = (
  selectedItems: Record<string, boolean>,
  criteria: MedicalNecessityDsm5Criteria[],
): string => {
  const sectionGroups: string[] = [];

  criteria.forEach((section) => {
    const selectedInSection: string[] = [];

    section.checklistItems.forEach((item) => {
      if (selectedItems[item.id]) {
        selectedInSection.push(item.title.toLowerCase());
      }
    });

    if (selectedInSection.length > 0) {
      sectionGroups.push(`${section.name} ${selectedInSection.join(", ")}`);
    }
  });

  return sectionGroups.join("; ");
};

export const convertStringToChecklist = (
  criteriaString: string | null | undefined,
  criteria: MedicalNecessityDsm5Criteria[],
): Record<string, boolean> => {
  const selectedItems: Record<string, boolean> = {};

  // Initialize all items to false
  criteria.forEach((section) => {
    section.checklistItems.forEach((item) => {
      selectedItems[item.id] = false;
    });
  });

  if (!criteriaString) return selectedItems;

  // Split by sections (separated by semicolons)
  const sections = criteriaString.split(";").map((s) => s.trim());

  sections.forEach((sectionText) => {
    criteria.forEach((section) => {
      // Check each item in the criteria against the full section text
      section.checklistItems.forEach((item) => {
        if (sectionText.toLowerCase().includes(item.title.toLowerCase())) {
          selectedItems[item.id] = true;
        }
      });
    });
  });

  return selectedItems;
};

export const parseMarkdown = (text: string) => {
  return text
    .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>") // Bold: **text**
    .replace(/\*(.*?)\*/g, "<i>$1</i>") // Italic: *text*
    .replace(/__(.*?)__/g, "<b>$1</b>") // Bold: __text__
    .replace(/_(.*?)_/g, "<i>$1</i>") // Italic: _text_
    .replace(/# (.*?)\n/g, "<h1>$1</h1>") // Header 1: # text
    .replace(/## (.*?)\n/g, "<h2>$1</h2>") // Header 2: ## text
    .replace(/### (.*?)\n/g, "<h3>$1</h3>") // Header 3: ### text
    .replace(/\n/g, "<br>"); // Newlines to <br>
};

export const truncateText = (text: string, wordCount: number = 50) => {
  const words = text.split(" ");
  if (words.length > wordCount) {
    return `${words.slice(0, wordCount).join(" ")}...`;
  }
  return text;
};

export const formatDuration = (startTime: number, endTime: number): string => {
  const duration = endTime - startTime;

  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const seconds = duration % 60;

  if (hours > 0) {
    return `${hours} hr ${minutes} min ${seconds} sec`;
  } else if (minutes > 0) {
    return `${minutes} min ${seconds} sec`;
  } else {
    return `${seconds} sec`;
  }
};

export const emptyFunction = () => Promise.resolve({});

/**
 * Returns true for dates that represent the CalendarPicker "All Time" sentinel (new Date(0)).
 * Checks year <= 1970 to catch timezone-shifted variants like "1969-12-31".
 */
export const isAllTimeSentinel = (date: Date): boolean => date.getFullYear() <= 1971;

export const downloadFileBlob = (file?: string, fileName?: string | null) => {
  if (!file) return;
  const byteCharacters = atob(file);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);

  // Create blob and download
  const blob = new Blob([byteArray], {
    type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  const fileNameToUse = fileName || "report";
  link.href = url;
  link.download = `${fileNameToUse}.docx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const DOCUMENT_MIME_TYPES: Record<string, string> = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

/** Downloads a base64 file under its own name; the type follows the extension (.pdf or .docx). */
export const downloadBase64Document = (file: string, name = "report.docx") => {
  const mimeType = DOCUMENT_MIME_TYPES[name.split(".").pop()?.toLowerCase() ?? ""] ?? DOCUMENT_MIME_TYPES.docx;
  const bytes = Uint8Array.from(atob(file), (character) => character.codePointAt(0) ?? 0);
  const url = window.URL.createObjectURL(new Blob([bytes], { type: mimeType }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

type Address = {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
};

export const formatAddress = (address: Address): string => {
  const { addressLine1, addressLine2, city, state, zip } = address;
  return [addressLine1, addressLine2, [city, state, zip].filter(Boolean).join(", ")].filter(Boolean).join("\n");
};

export const tabToName = (t: string) =>
  t
    .split("-")
    .map((w) => `${w[0].toUpperCase()}${w.slice(1)}`)
    .join(" ");

export const REPORT_TYPE_KEYS: Record<ReportTypeEnums, string> = {
  [ReportTypeEnums.DiagnosisEvaluation]: "Evaluation",
  [ReportTypeEnums.InitialAssessment]: "Initial Assessment",
  [ReportTypeEnums.Reassessment]: "Reassessment",
};

export const bytesToMB = (bytes: number, suffix: string = "mb"): string => {
  const bytesInMB = 1024 * 1024; // 1 MB = 1,048,576 bytes
  const mb = bytes / bytesInMB;
  return `${mb.toFixed(2)}${suffix}`;
};

export const normalizeTime = (timeStr: string) => {
  const parts = timeStr.split(":");
  if (parts.length === 2) {
    return `00:${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`; // mm:ss → 00:mm:ss
  } else if (parts.length === 3) {
    return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}:${parts[2].padStart(2, "0")}`;
  }
  return timeStr;
};

export const downloadReportPdfFile = async (s3Key: string, reportType: string, clientName: string = "Client") => {
  try {
    const response = await fetch(s3Key);

    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`);
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    // Extract the actual filename and extension from the S3 URL
    let filename = `${reportType} Report`;
    try {
      const url = new URL(s3Key);
      const pathname = url.pathname;
      const fileNameFromPath = pathname.split("/").pop();

      if (fileNameFromPath) {
        // Get the extension from the actual file
        const extension = fileNameFromPath.split(".").pop();

        const now = new Date();
        const dateString = now
          .toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "2-digit",
          })
          .replace(/\//g, "");

        filename = `${clientName} ${reportType} ${dateString}.${extension}`;
      }
    } catch (_urlError) {
      console.warn("Could not parse filename from URL, using default");
      // Fallback to default filename if URL parsing fails
      filename = `${reportType} Report.pdf`;
    }

    // Create download link and trigger download
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Revoke object URL to free memory
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Error downloading report file:", error);
    throw error;
  }
};

export const downloadSampleTemplatePdfFile = async (s3Url: string) => {
  try {
    const response = await fetch(s3Url);

    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`);
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const filename = `SampleReport.pdf`;

    // Create download link and trigger download
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Revoke object URL to free memory
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Error downloading report template file:", error);
    throw error;
  }
};

export function stringToColor(str: string, saturation: number = 60, lightness: number = 80): string {
  if (!str) return `hsl(-313, ${saturation}%, ${lightness}%)`; // not supposed to happen but dont crash
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  // Convert hash to HSL for nice pastel colors
  const hue = hash % 360;
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`; // background color
}

export function stringToTextColor(str: string) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = hash % 360;
  return `hsl(${hue}, 50%, 30%)`; // text color
}

export interface FileTypeColors {
  background: string;
  text: string;
}

export function fileTypeToColor(mimeType: string): FileTypeColors {
  if (mimeType.startsWith("application/")) {
    return { background: "bg-blue-100", text: "text-blue-600" };
  }
  if (mimeType.startsWith("audio/")) {
    return { background: "bg-green-100", text: "text-green-600" };
  }
  if (mimeType.startsWith("video/")) {
    return { background: "bg-orange-100", text: "text-orange-600" };
  }
  if (mimeType.startsWith("image/")) {
    return { background: "bg-limestone-100", text: "text-limestone-600" };
  }
  // Default: blurple
  return { background: "bg-blurple-100", text: "text-blurple-600" };
}

type Maybe<T> = T | null | undefined;
export function unwrapConnection<T>(connection: Maybe<{ edges?: Maybe<Maybe<{ node?: Maybe<T> }>[]> }>): T[] | null {
  if (!connection?.edges) return null;
  return connection.edges.map((edge) => edge?.node).filter((node): node is T => node != null);
}

export function openPopupWindow(url: string, target: string, width = 600, height = 700): Window | null {
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;
  return window.open(url, target, `width=${width},height=${height},left=${left},top=${top}`);
}

const LABEL_OVERRIDES: Record<string, string> = {
  AbaTherapy: "ABA Therapy",
  BCBS: "BCBS",
  BSW: "BSW",
  EMI_HEALTH: "EMI Health",
};

export const enumToCheckboxItems = (enumObj: Record<string, string>) =>
  Object.entries(enumObj).map(([key, value]) => ({
    id: value,
    title: LABEL_OVERRIDES[key] || toTitleCase(value.replace(/_/g, " ")),
  }));
